/**
 * E-mail sai do app de um jeito so: sem espaço nas pontas e em minúsculas.
 *
 * O login no Cognito diferencia maiúscula ("Maria@" e "maria@" são dois
 * usuários), e o teclado do celular põe a primeira letra em maiúscula sozinho.
 * Sem isto, o aluno cadastrado como "Reginaldo...@gmail.com" digitava
 * "reginaldo..." no primeiro acesso e ouvia que o cadastro não existia. A API
 * faz a mesma coisa do lado dela (`api/src/helpers/normalizar-email.ts`); aqui
 * é o que garante que o login no Cognito, que o app faz direto, também chegue
 * em minúsculas.
 */
export function normalizarEmail(email: string): string {
  return (email || "").trim().toLowerCase();
}

/*
  ─── O e-mail digitado errado ───

  Em 28/09/2026 um personal assinou o Pro pela página de vendas com
  "lfgb366@ooutlook.com", um "o" a mais. A conta nasceu com esse endereço, e no
  app, com o e-mail certo, a porta dizia "Conta não encontrada": ele pagou e não
  conseguia entrar. O erro está quase sempre no DOMÍNIO, e o domínio de quase
  todo mundo é um de uma dúzia de provedores. É contra eles que a digitação é
  conferida.

  Duas listas, com papéis diferentes:

  - ALVOS: para onde a correção pode apontar. Só provedor que existe e que o
    nosso público usa, porque sugerir um domínio que não existe é trocar um erro
    por outro.
  - LEGITIMOS: o que passa como está, sem sugestão. São os alvos mais os
    endereços reais que ficam PERTO de um alvo e não são erro: "mail.com" está a
    uma letra de "gmail.com", "r7.com" a duas de "me.com".
*/

const ALVOS = [
  "gmail.com",
  "hotmail.com",
  "hotmail.com.br",
  "outlook.com",
  "outlook.com.br",
  "live.com",
  "yahoo.com",
  "yahoo.com.br",
  "icloud.com",
  "uol.com.br",
  "bol.com.br",
  "terra.com.br",
];

const LEGITIMOS = new Set([
  ...ALVOS,
  "googlemail.com",
  "msn.com",
  "ymail.com",
  "rocketmail.com",
  "me.com",
  "mac.com",
  "ig.com.br",
  "globo.com",
  "globomail.com",
  "r7.com",
  "aol.com",
  "mail.com",
  "email.com",
  "hotmart.com",
  "gmx.com",
  "protonmail.com",
  "proton.me",
  "zoho.com",
  "yandex.com",
  "live.com.pt",
  "outlook.pt",
  "sapo.pt",
  "yahoo.com.ar",
  "hotmail.com.ar",
  "hotmail.es",
  "hotmail.fr",
  "hotmail.it",
  "outlook.es",
  "yahoo.es",
  "hotmail.co.uk",
  "yahoo.co.uk",
  "live.co.uk",
]);

/** Erros que a distância de letras não pega, porque o pedaço que falta é grande. */
const TROCAS_DIRETAS: Record<string, string> = {
  // O Gmail não tem ".com.br": todo endereço dele é gmail.com.
  "gmail.com.br": "gmail.com",
  // O UOL e o BOL só existem com ".br".
  "uol.com": "uol.com.br",
  "bol.com": "bol.com.br",
};

/** Quem escreveu só o nome do provedor, sem o final ("maria@gmail"). */
const SEM_FINAL: Record<string, string> = {
  gmail: "gmail.com",
  hotmail: "hotmail.com",
  outlook: "outlook.com",
  icloud: "icloud.com",
};

/** O fim do endereço digitado torto: ".con", ".cmo", ".combr". */
function corrigirFinal(dominio: string): string {
  return dominio
    .replace(/\.(con|cmo|ocm|vom|xom|cpm|cim|comm|coom)(\.br)?$/, ".com$2")
    .replace(/\.combr$/, ".com.br")
    .replace(/\.com\.(brr|b)$/, ".com.br");
}

/**
 * Quantas letras separam dois textos, contando letra trocada de lugar como UMA
 * (distância de Damerau na versão OSA): "gmial" e "gmail" estão a 1, e não a 2,
 * que é o erro de digitação mais comum que existe.
 */
function distancia(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const custo = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + custo);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

/**
 * O provedor que a pessoa provavelmente quis escrever, ou `null`.
 *
 * O NOME do provedor ("gmail") e o FINAL ("com.br") são medidos cada um no seu
 * pedaço. Medindo o domínio inteiro, o ".com.br" deixava todo domínio comprido e
 * liberava duas letras de diferença no nome: rodado nos e-mails de produção em
 * 28/09/2026, isso "corrigia" fahor.com.br (uma faculdade) para yahoo.com.br e
 * bemol.com.br (uma rede de lojas) para bol.com.br. Nome curto ("gmail",
 * "yahoo", "uol") aceita UMA letra de diferença; nome de seis letras ou mais
 * ("hotmail", "outlook", "icloud"), duas. O final aceita duas, porque é onde o
 * dedo escorrega ("gmail.comdd", "hotmail.xo").
 *
 * Empate entre dois provedores não sugere nada: "hol.com.br" está a uma letra do
 * UOL e do BOL, e escolher um seria chute.
 */
function provedorMaisPerto(dominio: string): string | null {
  const ponto = dominio.indexOf(".");
  if (ponto < 1) return null;
  const nome = dominio.slice(0, ponto);
  const final = dominio.slice(ponto + 1);

  let melhor: string | null = null;
  let menor = Infinity;
  let empate = false;
  for (const alvo of ALVOS) {
    const pontoDoAlvo = alvo.indexOf(".");
    const nomeDoAlvo = alvo.slice(0, pontoDoAlvo);
    const noNome = distancia(nome, nomeDoAlvo);
    const noFinal = distancia(final, alvo.slice(pontoDoAlvo + 1));
    if (noNome > (nomeDoAlvo.length >= 6 ? 2 : 1) || noFinal > 2) continue;
    const total = noNome + noFinal;
    if (total === 0 || total > 3) continue;
    if (total < menor) {
      menor = total;
      melhor = alvo;
      empate = false;
    } else if (total === menor) {
      empate = true;
    }
  }
  return melhor && !empate ? melhor : null;
}

/** Um provedor conhecido, do jeito que está ou pela distância. */
function provedorDe(dominio: string): string | null {
  if (LEGITIMOS.has(dominio)) return dominio;
  return TROCAS_DIRETAS[dominio] ?? provedorMaisPerto(dominio);
}

/**
 * O e-mail que a pessoa provavelmente quis escrever, ou `null` quando o que
 * está escrito parece certo.
 *
 * Só olha o que vem depois do @: o nome de antes é da pessoa, e ninguém tem
 * como saber se "joao.silva" devia ser "joaosilva". E só SUGERE: quem decide é
 * a pessoa, porque existe domínio de verdade que parece erro (o da empresa
 * dela, por exemplo).
 *
 * | Escrito | Sugestão |
 * |---------|----------|
 * | maria@ooutlook.com | maria@outlook.com |
 * | maria@gmial.com | maria@gmail.com |
 * | maria@hotmail.con | maria@hotmail.com |
 * | maria@gmail.com.br | maria@gmail.com |
 * | maria@gmail | maria@gmail.com |
 * | maria@04gmail.com | maria04@gmail.com |
 * | maria@studio.con | maria@studio.com |
 * | maria@studioforma.com.br | (nada) |
 * | maria@fahor.com.br | (nada: é uma faculdade, e não o Yahoo) |
 */
export function sugestaoDeEmail(email: string): string | null {
  const limpo = normalizarEmail(email).replace(/\s/g, "");
  const arroba = limpo.lastIndexOf("@");
  if (arroba < 1 || arroba === limpo.length - 1) return null;

  const nome = limpo.slice(0, arroba);
  const escrito = limpo.slice(arroba + 1);
  // Vírgula no lugar do ponto (teclado do celular), ponto dobrado e ponto sobrando nas pontas.
  const arrumado = escrito.replace(/,/g, ".").replace(/\.{2,}/g, ".").replace(/^\.+|\.+$/g, "");
  if (!arrumado) return null;

  const sugerir = (dominio: string) => (dominio === escrito ? null : `${nome}@${dominio}`);

  if (LEGITIMOS.has(arrumado)) return sugerir(arrumado);
  if (TROCAS_DIRETAS[arrumado]) return sugerir(TROCAS_DIRETAS[arrumado]);

  if (!arrumado.includes(".")) {
    for (const [provedor, dominio] of Object.entries(SEM_FINAL)) {
      if (distancia(arrumado, provedor) <= 1) return sugerir(dominio);
    }
    return null;
  }

  const comFinal = corrigirFinal(arrumado);

  // Um ponto a mais no meio do nome ("g.mail.com").
  const partes = comFinal.split(".");
  for (let i = 1; i < partes.length - 1; i++) {
    const semOPonto = [...partes.slice(0, i - 1), partes[i - 1] + partes[i], ...partes.slice(i + 1)].join(".");
    if (ALVOS.includes(semOPonto)) return sugerir(semOPonto);
  }

  /* Número colado no provedor ("maria@04gmail.com"): o número é do NOME, que
     escorregou para depois do @. Tirar o número daria "maria@gmail.com", que é a
     caixa de outra pessoa; o provável é "maria04@gmail.com". Domínio de verdade
     que começa com número ("3m.com") não cai aqui, porque o resto dele não é um
     provedor. */
  const numeroNaFrente = comFinal.match(/^(\d+)([a-z].*)$/);
  if (numeroNaFrente) {
    const provedor = provedorDe(numeroNaFrente[2]);
    if (provedor) return `${nome}${numeroNaFrente[1]}@${provedor}`;
  }

  const provedor = provedorDe(comFinal);
  if (provedor) return sugerir(provedor);

  // Domínio que não é de provedor (o da empresa): só o final torto é corrigido.
  return sugerir(comFinal);
}
